import { useState } from 'react';
import { toast } from '../../../common/toast';
import { cancelOffers } from './cancelOfferService';
import { usePersistedDataStore } from '../../store';
import { useUser } from '../../UserContext';
import logger from '../../../utils/logger';

/**
 * Unified hook for handling offer/settlement cancellation
 * Can be used by any component that needs to cancel offers or settlements
 */
export const useCancelOffers = () => {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingOffers, setCancellingOffers] = useState(false);
  const { user } = useUser();

  // Get store functions
  const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
  const setNegotiationObj = usePersistedDataStore.getState().setNegotiationObj;
  const setNegotiationObjVersion = usePersistedDataStore.getState().setNegotiationObjVersion;

  const openCancelDialog = () => {
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
  };

  const handleConfirmCancel = async (rowsToCancel) => {
    setCancellingOffers(true);
    
    try {
      await cancelOffers({
        selectedRows: rowsToCancel,
        getNegotiationObj,
        setNegotiationObj,
        setNegotiationObjVersion,
        user
      });

      // Success message
      const caseCount = rowsToCancel.length;
      const message = caseCount === 1 
        ? `Successfully cancelled the settlement for ${rowsToCancel[0].Address || rowsToCancel[0].parcel_id}.`
        : `Successfully cancelled settlements for ${caseCount} cases.`;
      
      toast.success(message);
      setCancelDialogOpen(false);
    } catch (error) {
      logger.error('Error canceling settlement:', error);
    } finally {
      setCancellingOffers(false);
    }
  };

  const handleConfirmCancelSingle = async (subjectParcelId, caseAddress) => {
    setCancellingOffers(true);
    
    try {
      await cancelOffers({
        subjectParcelId,
        getNegotiationObj,
        setNegotiationObj,
        setNegotiationObjVersion,
        user
      });

      toast.success(`Successfully cancelled the settlement for ${caseAddress || subjectParcelId}.`);
      setCancelDialogOpen(false);
    } catch (error) {
      logger.error('Error canceling settlement:', error);
    } finally {
      setCancellingOffers(false);
    }
  };

  return {
    // State
    cancelDialogOpen,
    cancellingOffers,
    
    // Actions
    openCancelDialog,
    closeCancelDialog,
    handleConfirmCancel,
    handleConfirmCancelSingle,
    
    // Store access
    getNegotiationObj
  };
};
